import { useCallback, useMemo } from 'react';
import { orderBy } from 'lodash';
import { normalizePinnedItems, PINNED_FIRMS_KEY, PinnedItemId } from 'common/constants/userPreferences';
import { useLocalStorageByUser } from 'common/hooks';
import { Text } from 'components/ui';
import FirmOption from 'pages/Auth/components/FirmOption';
import type { FirmListProps, LoginFirm } from './FirmList.props';

const FirmList = ({ firmList, searchState }: FirmListProps) => {
  const [pinnedFirmsRaw, setPinnedFirms] = useLocalStorageByUser<PinnedItemId[]>(PINNED_FIRMS_KEY, []);
  const pinnedFirms = useMemo(() => normalizePinnedItems(pinnedFirmsRaw), [pinnedFirmsRaw]);

  const updatePinStatus = useCallback(
    (id: number, _title: string, isPinned: boolean) => {
      let nextPinnedOptions: PinnedItemId[];

      if (isPinned) {
        nextPinnedOptions = pinnedFirms.filter(option => option.value !== id) ?? [];
      } else {
        nextPinnedOptions = [...pinnedFirms, { value: id }];
      }

      setPinnedFirms(nextPinnedOptions);
    },
    [pinnedFirms, setPinnedFirms]
  );

  const renderFirmOption = (firm: LoginFirm) => (
    <FirmOption key={firm.id} iconUrl={firm.icon_url ?? undefined} {...firm} />
  );

  const processedFirmList = useMemo(() => {
    if (!firmList) {
      return [];
    }

    return firmList.map((firm: LoginFirm) => {
      const isFirmPinned = Boolean(pinnedFirms.find(pinnedFirm => pinnedFirm.value === firm.id));

      return {
        ...firm,
        isPinnable: true,
        isPinned: isFirmPinned,
        updatePinStatus: (id: number, title: string, isPinned: boolean) => updatePinStatus(id, title, isPinned),
      };
    });
  }, [firmList, pinnedFirms, updatePinStatus]);

  if (!firmList || firmList.length === 0) {
    return null;
  }

  // Sort by pinned status (pinned items first)
  const sortedFirms = orderBy(processedFirmList, ['isPinned'], ['desc']);

  if (!searchState) {
    // if Search is empty return the full list;
    return (
      <>
        {sortedFirms.map(renderFirmOption)}
      </>
    );
  }

  const filteredList = sortedFirms.filter((firm: LoginFirm) =>
    firm.name.toLowerCase().includes(searchState.toLowerCase())
  );

  if (filteredList.length === 0) {
    return <Text variant="h4">FIRM NOT FOUND</Text>;
  }

  return (
    <>
      {filteredList.map(renderFirmOption)}
    </>
  );
};

export default FirmList;
